import React from 'react';
import { Card, Row, Col, Typography, Divider, Alert, Button, Space } from 'antd';
import { 
  FileExcelOutlined, 
  UploadOutlined, 
  DownloadOutlined, 
  TableOutlined 
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const Demo: React.FC = () => {
  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Title level={1}>检查报告管理系统 - 功能演示</Title>
        
        <Alert
          message="系统已实现完整的导入、评定、展示、导出功能"
          description="支持Excel模板导入，自动评定分析，可视化展示，灵活导出等功能"
          type="success"
          showIcon
          style={{ marginBottom: '24px' }}
        />

        <Row gutter={[24, 24]}>
          <Col span={12}>
            <Card 
              title={
                <Space>
                  <UploadOutlined />
                  <span>导入功能</span>
                </Space>
              } 
              size="small"
            >
              <Paragraph>
                <Text strong>功能特点：</Text>
                <ul>
                  <li>提供Excel导入模板下载</li>
                  <li>支持批量数据解析和验证</li>
                  <li>实时显示解析进度</li>
                  <li>数据预览和错误检查</li>
                </ul>
              </Paragraph>
              
              <Paragraph>
                <Text strong>支持字段：</Text>
                <ul>
                  <li>业务所属创大</li>
                  <li>培训业务名称</li>
                  <li>培训要素</li>
                  <li>本次检查结果</li>
                  <li>主体分级（城市等级）</li>
                  <li>负责人</li>
                  <li>检查时间</li>
                </ul>
              </Paragraph>
              
              <Button type="primary" href="/import-new">
                <UploadOutlined /> 体验导入功能
              </Button>
            </Card>
          </Col>
          
          <Col span={12}>
            <Card 
              title={
                <Space>
                  <TableOutlined />
                  <span>评定规则</span>
                </Space>
              } 
              size="small"
            >
              <Paragraph>
                <Text strong>讲师课酬评定：</Text>
                <ul>
                  <li>一二线城市：对比最大值</li>
                  <li>其他城市：对比中间值</li>
                  <li>高于标准10%以上：<Text style={{color: '#f5222d'}}>红色↑</Text></li>
                  <li>高于标准10%以内：<Text style={{color: '#fa8c16'}}>橙色↑</Text></li>
                  <li>低于标准10%以内：<Text style={{color: '#52c41a'}}>绿色↓</Text></li>
                  <li>低于标准10%以上：<Text style={{color: '#1890ff'}}>蓝色↓</Text></li>
                </ul>
              </Paragraph>
              
              <Paragraph>
                <Text strong>其他培训要素：</Text>
                <ul>
                  <li>不区分城市等级</li>
                  <li>统一按照最小值-最大值范围评定</li>
                  <li>颜色规则与讲师课酬一致</li>
                </ul>
              </Paragraph>
            </Card>
          </Col>
        </Row>

        <Divider />

        <Row gutter={[24, 24]}>
          <Col span={12}>
            <Card 
              title={
                <Space>
                  <TableOutlined />
                  <span>展示功能</span>
                </Space>
              } 
              size="small"
            >
              <Paragraph>
                <Text strong>数据展示：</Text>
                <ul>
                  <li>表格形式展示所有检查数据</li>
                  <li>支持按业务、城市等级筛选</li>
                  <li>可视化评定结果（颜色+箭头）</li>
                  <li>支持在线编辑评定信息</li>
                  <li>支持批量选择操作</li>
                </ul>
              </Paragraph>
              
              <Paragraph>
                <Text strong>交互功能：</Text>
                <ul>
                  <li>点击编辑评定信息和备注</li>
                  <li>实时搜索和过滤</li>
                  <li>分页浏览</li>
                  <li>排序功能</li>
                </ul>
              </Paragraph>
              
              <Button type="primary" href="/reports-new">
                <TableOutlined /> 查看报告管理
              </Button>
            </Card>
          </Col>
          
          <Col span={12}>
            <Card 
              title={
                <Space>
                  <DownloadOutlined />
                  <span>导出功能</span>
                </Space>
              } 
              size="small"
            >
              <Paragraph>
                <Text strong>导出格式：</Text>
                <ul>
                  <li>Excel格式(.xlsx)</li>
                  <li>包含完整的数据信息</li>
                  <li>保留颜色和样式</li>
                  <li>支持批量导出</li>
                </ul>
              </Paragraph>
              
              <Paragraph>
                <Text strong>导出内容：</Text>
                <ul>
                  <li>基本检查信息</li>
                  <li>参考范围</li>
                  <li>评定结果和提示</li>
                  <li>备注信息</li>
                  <li>统计汇总（可选）</li>
                </ul>
              </Paragraph>
            </Card>
          </Col>
        </Row>

        <Divider />

        <Card title="使用流程" size="small">
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <div style={{ textAlign: 'center', padding: '16px' }}>
                <FileExcelOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                <div style={{ marginTop: '8px' }}>
                  <Text strong>1. 下载模板</Text>
                </div>
                <Text type="secondary">获取标准导入模板</Text>
              </div>
            </Col>
            
            <Col span={6}>
              <div style={{ textAlign: 'center', padding: '16px' }}>
                <UploadOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
                <div style={{ marginTop: '8px' }}>
                  <Text strong>2. 导入数据</Text>
                </div>
                <Text type="secondary">上传填写完整的Excel</Text>
              </div>
            </Col>
            
            <Col span={6}>
              <div style={{ textAlign: 'center', padding: '16px' }}>
                <TableOutlined style={{ fontSize: '24px', color: '#fa8c16' }} />
                <div style={{ marginTop: '8px' }}>
                  <Text strong>3. 查看分析</Text>
                </div>
                <Text type="secondary">系统自动评定分析</Text>
              </div>
            </Col>
            
            <Col span={6}>
              <div style={{ textAlign: 'center', padding: '16px' }}>
                <DownloadOutlined style={{ fontSize: '24px', color: '#722ed1' }} />
                <div style={{ marginTop: '8px' }}>
                  <Text strong>4. 导出报告</Text>
                </div>
                <Text type="secondary">生成完整检查报告</Text>
              </div>
            </Col>
          </Row>
        </Card>

        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <Space size="large">
            <Button type="primary" size="large" href="/import-new">
              <UploadOutlined /> 开始导入数据
            </Button>
            <Button size="large" href="/reports-new">
              <TableOutlined /> 查看报告管理
            </Button>
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default Demo;